import { useCallback, useMemo } from 'react';
import { List } from 'react-virtualized';
import { Text } from 'foundations-components/components/Text';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { useRelationalReportInfrastructure, useRelationalReportProperties, useRelationalReportAdvancedProperties } from 'report-customization-components/contexts/RelationalReportContext';
import { getNewColumnFromPropertyValue, getPropertyColumnByValue, propertyIsChecked, getDefaultExpressionByPropertyValue, removalIsDisabled } from 'report-customization-components/lib/relational-report-utils/property-utils';
import { SnowflakeReportUpdateActions } from 'reporting-snowflake/relational/updaters/report/use-snowflake-updater';
import { jsx as _jsx } from "react/jsx-runtime";
const ROW_WIDTH = 420;
const ROW_HEIGHT = 32;
const LIST_HEIGHT = 320;
const OVERSCAN_ROW_COUNT = 50;
const VirtualizedPropertyCheckboxList = ({
  report,
  relationalReport,
  snowflakeUpdater,
  allAvailableColumns,
  currentAllColumns,
  onChange,
  groupedOptions,
  tableAndPropertyMeta
}) => {
  const {
    allDefaultExpressions,
    allDefaultPropertyData,
    formulaFieldExpressions,
    allDatasetFields
  } = useRelationalReportAdvancedProperties();
  const onCheckboxClick = useCallback(propertyValue => ({
    target: {
      checked
    }
  }) => {
    if (!checked) {
      const column = getPropertyColumnByValue(propertyValue, allAvailableColumns);
      const updatedReportDefinition = snowflakeUpdater(SnowflakeReportUpdateActions.REMOVE_COLUMN_FROM_STAGED_COLUMNS, {
        column
      });
      if (!updatedReportDefinition.error) {
        const updatedReport = report.set('reportDefinition', updatedReportDefinition.report.toMap());
        onChange(updatedReport);
      }
    } else {
      const column = getNewColumnFromPropertyValue(propertyValue, tableAndPropertyMeta, allDefaultExpressions, allDefaultPropertyData, relationalReport, allDatasetFields);
      const expressionField = getDefaultExpressionByPropertyValue(propertyValue, allDefaultExpressions);
      const updatedReportDefinition = snowflakeUpdater(SnowflakeReportUpdateActions.ADD_COLUMN_TO_STAGED_COLUMNS_WITH_EXPRESSION, {
        column,
        expressionField
      });
      if (!updatedReportDefinition.error) {
        const updatedReport = report.set('reportDefinition', updatedReportDefinition.report.toMap());
        onChange(updatedReport);
      }
    }
  }, [allAvailableColumns, onChange, report, snowflakeUpdater, allDefaultExpressions, allDefaultPropertyData, tableAndPropertyMeta, relationalReport, allDatasetFields]);
  const flattenedOptions = useMemo(() => {
    const flattened = groupedOptions.map(({
      options,
      text
    }) => {
      return [text, ...options];
    }).flat();
    if (flattened[0] === '') {
      flattened.shift();
    }
    return flattened;
  }, [groupedOptions]);
  const renderRow = ({
    index,
    key,
    style
  }) => {
    const option = flattenedOptions[index];
    if (typeof option === 'string') {
      return (
        /*#__PURE__*/
        // @bend-expect trellis-tokenized
        // The style attribute is required by react-virtualized for positioning and should not be problematic for the migration
        _jsx("div", {
          style: style,
          children: /*#__PURE__*/_jsx(Text, {
            typography: "heading-100",
            marginBlock: "100",
            children: option
          })
        }, key)
      );
    }
    const isDisabled = removalIsDisabled(option === null || option === void 0 ? void 0 : option.value, currentAllColumns, formulaFieldExpressions);
    return (
      /*#__PURE__*/
      // @bend-expect trellis-tokenized
      // The style attribute is required by react-virtualized for positioning and should not be problematic for the migration
      _jsx("div", {
        style: style,
        children: /*#__PURE__*/_jsx(Tooltip, {
          disabled: !isDisabled,
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "report-customization-components.customization-components.data-columns-modal.config.disabledProperty"
          }),
          placement: "top",
          children: /*#__PURE__*/_jsx(Checkbox, {
            onChange: onCheckboxClick(option === null || option === void 0 ? void 0 : option.value),
            checked: propertyIsChecked(option === null || option === void 0 ? void 0 : option.value, allAvailableColumns),
            disabled: isDisabled,
            children: /*#__PURE__*/_jsx(TruncateString, {
              children: option === null || option === void 0 ? void 0 : option.text
            })
          })
        })
      }, key)
    );
  };
  return /*#__PURE__*/_jsx(List, {
    width: ROW_WIDTH,
    height: LIST_HEIGHT,
    rowHeight: ROW_HEIGHT,
    rowRenderer: renderRow,
    rowCount: flattenedOptions.length,
    overscanRowCount: OVERSCAN_ROW_COUNT
  });
};
const RelationalReportPropertyOptionsPanel = ({
  report,
  onChange,
  searchValue,
  localRelationalReport,
  localRelationalUpdater,
  localAllAvailableColumns,
  localCurrentAllColumns
}) => {
  const {
    allPropertiesOptions
  } = useRelationalReportProperties();
  const {
    fullTableAndPropertyMeta: {
      data: tableAndPropertyMeta,
      error
    }
  } = useRelationalReportInfrastructure();
  if (error) {
    return /*#__PURE__*/_jsx("p", {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "report-customization-components.customization-components.data-columns-modal.config.error"
      })
    });
  }
  if (!tableAndPropertyMeta) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      "data-test-id": "properties-loading",
      layout: "centered"
    });
  }
  const filteredPropertyOptions = allPropertiesOptions.map(group => {
    const filteredOptions = group.options.filter(option => {
      var _option$text, _option$text$toLowerC, _option$text$toLowerC2, _option$text$toLowerC3;
      return !searchValue || searchValue.length < 2 || ((_option$text = option.text) === null || _option$text === void 0 || (_option$text$toLowerC = _option$text.toLowerCase) === null || _option$text$toLowerC === void 0 || (_option$text$toLowerC2 = (_option$text$toLowerC3 = _option$text$toLowerC.call(_option$text)).includes) === null || _option$text$toLowerC2 === void 0 ? void 0 : _option$text$toLowerC2.call(_option$text$toLowerC3, searchValue.toLowerCase()));
    });
    return Object.assign({}, group, {
      options: filteredOptions
    });
  }).filter(group => {
    return !!group.options.length;
  });
  return /*#__PURE__*/_jsx(VirtualizedPropertyCheckboxList, {
    report: report,
    relationalReport: localRelationalReport,
    snowflakeUpdater: localRelationalUpdater,
    allAvailableColumns: localAllAvailableColumns,
    currentAllColumns: localCurrentAllColumns,
    onChange: onChange,
    groupedOptions: filteredPropertyOptions,
    tableAndPropertyMeta: tableAndPropertyMeta
  });
};
export default RelationalReportPropertyOptionsPanel;